import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { IoMdArrowRoundBack } from "react-icons/io";
import { MdEdit } from "react-icons/md";
import { FiChevronDown } from "react-icons/fi";
import { FaCheck, FaTimes } from "react-icons/fa";
import {
  getAllUsers,
  approveUserById,
  bulkUpdateUsers,
  getUser,
} from "../../Services/userService.js";
import {
  Pagination,
  AccessDenied,
  Spinner,
  GenericTable,
} from "../../Components/Common";
import { TOAST_MESSAGES, ROLE } from "../../Constants/Strings";
import {
  STATUS_TYPES,
  USER_COLUMNS,
  USER_FORM_LABELS,
  USER_COLUMN_WIDTHS,
} from "./constants";
import { UserContext } from "../../Context/UserContext";
import { extractErrorMessage } from "../../Utils/extractErrorMessage.js";

const UserList = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const { user: contextUser, isHeadOfficeAdmin } = useContext(UserContext);
  const [loggedInUser, setLoggedInUser] = useState(null);

  // Pagination states
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const onPageSizeChange = (newSize) => {
    setRowsPerPage(newSize);
    setPage(1);
  };

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Checkbox state
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Status and Role filter states
  const [activeTab, setActiveTab] = useState(STATUS_TYPES.ACTIVE);
  const [roleFilter, setRoleFilter] = useState(null);

  const [roleOpen, setRoleOpen] = useState(false);
  const roleRef = useRef();

  // Bulk update
  const [bulkRole, setBulkRole] = useState("");
  const [bulkStatus, setBulkStatus] = useState("");

  const [bulkRoleOpen, setBulkRoleOpen] = useState(false);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);

  const bulkRoleRef = useRef();
  const bulkStatusRef = useRef();

  // Fetch users with pagination
  const fetchUsers = async (page = 1) => {
    try {
      setTableLoading(true);
      const offset = (page - 1) * rowsPerPage;

      let approvedFilter = activeTab === STATUS_TYPES.ACTIVE;

      let roleValue =
        roleFilter === null || roleFilter === "ALL" ? undefined : roleFilter;

      const res = await getAllUsers({
        limit: rowsPerPage,
        offset,
        is_approved: approvedFilter,
        role: roleValue,
      });

      if (res.status === 200) {
        setUserData(res.data.results || []);
        setTotalPages(Math.max(1, Math.ceil(res.data.count / rowsPerPage)));
        setTotalCount(res.data.count || 0); // store total count
        // setSelectedUsers([]); // reset selection on page change
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.FAILED_DELETE_FILE;
        toast.error(msg);
      }
    } catch (err) {
      const msg =
        extractErrorMessage(err) || TOAST_MESSAGES.ERROR_FETCHING_USERS;
      toast.error(msg);
    } finally {
      setTableLoading(false);
    }
  };

  const loadLoggedInUser = async () => {
    try {
      const res = await getUser();
      if (res.status === 200) {
        setLoggedInUser(res.data);
      }
    } catch (err) {
      const msg =
        extractErrorMessage(err) || TOAST_MESSAGES.LOGGED_IN_USER_FETCH_FAILED;
      toast.error(msg);
    }
  };

  useEffect(() => {
    loadLoggedInUser();
  }, [contextUser]);

  useEffect(() => {
    if (isHeadOfficeAdmin) {
      fetchUsers(page);
    }
  }, [isHeadOfficeAdmin, page, rowsPerPage, roleFilter, activeTab]);

  // Handle edit action
  const handleEdit = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleSaveChanges = async () => {
    try {
      const payload = {
        id: selectedUser.id,
        role: selectedUser.role,
        is_approved: selectedUser.is_approved,
      };

      const response = await approveUserById(payload);

      if (response.status === 200 || response.status === 204) {
        toast.success(TOAST_MESSAGES.USER_UPDATED);
        fetchUsers();
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.USER_UPDATION_FAILED;
        toast.error(msg);
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.USER_UPDATION_ERROR;
      toast.error(msg);
    } finally {
      setIsEditModalOpen(false);
    }
  };

  // Checkbox Handlers
  const handleSelectUser = (id) => {
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((userId) => userId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const excludeId = loggedInUser?.id;
    const allSelectableUsers = userData
      .filter(
        (u) =>
          !(
            u.role === ROLE.HEAD_OFFICE.value &&
            String(u.id) === String(excludeId)
          )
      )
      .map((u) => u.id);

    if (allSelectableUsers.every((id) => selectedUsers.includes(id))) {
      setSelectedUsers((prev) =>
        prev.filter((id) => !allSelectableUsers.includes(id))
      ); // Unselect only current page users
    } else {
      setSelectedUsers((prev) => [
        ...new Set([...prev, ...allSelectableUsers]),
      ]); // Add current page users to global selection
    }
  };

  // Bulk Update Handler
  const handleBulkUpdate = async () => {
    if (selectedUsers.length === 0) {
      toast.error(TOAST_MESSAGES.NO_USER_SELECTION);
      return;
    }

    if (!bulkRole && !bulkStatus) {
      toast.error(TOAST_MESSAGES.HANDLE_BULK_UPDATE);
      return;
    }

    const filteredIds = selectedUsers.filter((id) => {
      const rowUser = userData.find((u) => u.id === id);
      return !(
        isHeadOfficeAdmin &&
        contextUser?.id &&
        rowUser?.role === ROLE.HEAD_OFFICE &&
        String(rowUser.id) === String(contextUser.id)
      );
    });

    if (filteredIds.length === 0) {
      toast.error(TOAST_MESSAGES.NO_VALID_SELECTED_USER_BULK_UPDATE);
      return;
    }

    try {
      // payload for bulk API
      const payload = {
        ids: filteredIds,
        ...(bulkRole && { role: bulkRole }),
        ...(bulkStatus && { is_approved: bulkStatus === "true" }),
      };

      await bulkUpdateUsers(payload);
      toast.success(TOAST_MESSAGES.BULK_UPDATION_SUCCESS);

      // Refresh list
      fetchUsers(page);

      // Reset Selection
      setSelectedUser([]);
      setBulkRole("");
      setBulkStatus("");
    } catch {
      toast.error(TOAST_MESSAGES.BULK_UPDATION_FAILURE);
    }
  };

  const handleClickOutside = (e) => {
    if (roleRef.current && !roleRef.current.contains(e.target)) {
      setRoleOpen(false);
    }
    if (bulkRoleRef.current && !bulkRoleRef.current.contains(e.target)) {
      setBulkRoleOpen(false);
    }
    if (bulkStatusRef.current && !bulkStatusRef.current.contains(e.target)) {
      setBulkStatusOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!isHeadOfficeAdmin) {
    return <AccessDenied />;
  }

  const tableColumns = [
    "checkbox",
    ...USER_COLUMNS.map((c) => c.key),
    "actions",
  ];

  const columnLabels = {
    checkbox: (
      <input
        type="checkbox"
        checked={
          userData.length > 0 && selectedUsers.length === userData.length
        }
        onChange={handleSelectAll}
        className="rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 focus:outline-none"
      />
    ),

    ...USER_COLUMNS.reduce((acc, col) => {
      acc[col.key] = col.label;
      return acc;
    }, {}),

    actions: "Action",
  };

  const renderField = (key, row) => {
    const content = (() => {
      switch (key) {
        case "checkbox":
          const disableCheckbox =
            row.role === ROLE.HEAD_OFFICE.value &&
            String(row.id) === String(loggedInUser?.id);
          return (
            <input
              type="checkbox"
              checked={selectedUsers.includes(row.id)}
              onChange={() => !disableCheckbox && handleSelectUser(row.id)}
              disabled={disableCheckbox}
              className={`rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 focus:outline-none
              ${disableCheckbox ? "opacity-50 cursor-not-allowed" : ""}`}
              title={disableCheckbox ? "Cannot select yourself" : ""}
            />
          );

        case "name":
          return (
            <span className="font-medium text-gray-900 whitespace-nowrap">
              {row.first_name} {row.last_name}
            </span>
          );

        case "email":
          return (
            <span className="truncate" title={row.email || ""}>
              {row.email || "-"}
            </span>
          );

        case "mobile":
          return row.mobile || "-";

        case "role":
          return (
            Object.values(ROLE).find((role) => role.value === row.role)
              ?.label || "-"
          );

        case "status":
          return (
            <span
              className={`${
                row.is_approved ? "text-green-600" : "text-red-600"
              } font-medium`}
            >
              {row.is_approved ? STATUS_TYPES.ACTIVE : STATUS_TYPES.DEACTIVE}
            </span>
          );

        case "actions":
          const disableEdit =
            row.role === ROLE.HEAD_OFFICE.value &&
            String(row.id) === String(loggedInUser?.id);
          return (
            <MdEdit
              size={20}
              className={`${
                disableEdit
                  ? "text-gray-600 opacity-50 cursor-not-allowed"
                  : "text-primary cursor-pointer"
              }`}
              title={disableEdit ? "Cannot edit yourself" : "Edit"}
              onClick={() => !disableEdit && handleEdit(row)}
            />
          );

        default:
          return row[key] ?? "-";
      }
    })();

    return (
      <div
        className={`${USER_COLUMN_WIDTHS[key] || ""} truncate`}
        title={typeof content === "string" ? content : undefined}
      >
        {content}
      </div>
    );
  };

  const renderMobileCard = (row, renderField) => {
    const disableCheckbox =
      row.role === ROLE.HEAD_OFFICE.value &&
      String(row.id) === String(loggedInUser?.id);
    return (
      <div className="p-3 pl-0 m-2 ml-0 text-sm bg-white dark:bg-gray-900 border-b-2 ">
        <div className="grid grid-cols-6 gap-1 items-center">
          <div className="col-span-1 flex justify-center">
            <input
              type="checkbox"
              checked={selectedUsers.includes(row.id)}
              onChange={() => handleSelectUser(row.id)}
              disabled={disableCheckbox}
              className={`rounded border-gray-300 text-blue-600 focus:outline-none focus:ring-0
                  ${disableCheckbox ? "opacity-50 cursor-not-allowed" : ""}`}
              title={disableCheckbox ? "Cannot select yourself" : ""}
            />
          </div>

          <div className="col-span-2">
            <span className="font-bold text-gray-900 dark:text-white truncate block">
              {row.first_name} {row.last_name}
            </span>
          </div>

          <div className="col-span-2">
            <span className="text-gray-700 dark:text-gray-300 truncate block">
              {Object.values(ROLE).find((role) => role.value === row.role)
                ?.label || "-"}
            </span>
          </div>

          <div className="col-span-1 flex justify-center">
            {renderField("actions", row)}
          </div>

          <div className="col-span-1"></div>

          <div className="col-span-2">
            <span className="text-gray-600 dark:text-gray-400 block">
              {row.mobile || "-"}
            </span>
          </div>

          <div className="col-span-2">
            <span
              className={`block font-medium ${
                row.is_approved ? "text-green-600" : "text-red-600"
              }`}
            >
              {row.is_approved ? STATUS_TYPES.ACTIVE : STATUS_TYPES.DEACTIVE}
            </span>
          </div>

          <div className="col-span-1"></div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex gap-2 md:gap-4 sm:px-2 pt-2 pb-1 items-center">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-center text-xs md:text-sm font-medium pt-2 md:p-3"
        >
          <IoMdArrowRoundBack size={18} className="text-black" />
        </button>
        <h1 className="text-black text-lg md:text-2xl font-semibold pt-2 md:py-3">
          Manage Users
        </h1>
      </div>

      <div className="flex-1 flex flex-col overflow-y-auto">
        {/* Tabs + Filters Row */}
        <div className="flex justify-between items-center px-2 md:px-4">
          {/* Tabs on Left */}
          <div className="flex space-x-6">
            {[STATUS_TYPES.ACTIVE, STATUS_TYPES.DEACTIVE].map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  setPage(1);
                }}
                className={`px-4 py-1 font-bold border-b-2 text-sm md:text-base ${
                  activeTab === tab
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-600 hover:text-gray-800"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="flex gap-2 md:gap-8 items-center text-sm md:text-base">
            <span className="font-bold text-gray-600">Filters:</span>
            {/* Role Filter */}
            <div ref={roleRef} className="relative">
              <button
                onClick={() => {
                  setRoleOpen((r) => !r);
                }}
                className={`inline-flex items-center px-0 pt-1 text-sm md:text-base ${
                  roleOpen
                    ? "border-b-2 border-blue-500 text-primary"
                    : "border-b-2 border-transparent text-gray-700 hover:border-gray-400"
                } focus:outline-none`}
              >
                {roleFilter
                  ? Object.values(ROLE).find(
                      (role) => role.value === roleFilter
                    )?.label
                  : "Role"}
                <FiChevronDown
                  className={`w-5 h-5 ml-2 transform transition-transform duration-200 ${
                    roleOpen ? "rotate-180" : "rotate-0"
                  }`}
                />
              </button>

              <div
                className={`absolute right-0 z-50 mt-2 min-w-full md:w-48 bg-white divide-y divide-gray-100 rounded-lg shadow transition-all duration-200 origin-top ${
                  roleOpen
                    ? "opacity-100 scale-100 pointer-events-auto"
                    : "opacity-0 scale-95 pointer-events-none"
                } max-h-60 overflow-auto`}
              >
                <ul className="py-2 text-base text-gray-700">
                  {Object.values(ROLE).map((role) => (
                    <li key={role.value}>
                      <button
                        className={`w-full px-4 py-2 text-left hover:bg-gray-100 ${
                          roleFilter === role.value
                            ? "bg-gray-100 font-medium"
                            : ""
                        }`}
                        onClick={() => {
                          setRoleFilter(role.value);
                          setPage(1);
                          setRoleOpen(false);
                        }}
                      >
                        {role.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Bulk Update Section */}

        <div
          className={`px-5 md:px-8 md:mt-2 mt-4 flex flex-col md:flex-row items-start md:items-center gap-1 md:gap-6 text-sm md:text-base transition-all duration-200 ease-in-out
            ${
              selectedUsers.length > 0
                ? "max-h-36 opacity-100"
                : "max-h-0 opacity-0"
            }
          `}
        >
          <div className="flex flex-row items-center gap-2 sm:gap-4">
            <span className="font-bold text-gray-600 text-justify">
              Bulk Update:
            </span>
            <span className="text-primary text-sm font-semibold md:hidden">
              {selectedUsers.length}/{totalCount} selected
            </span>
          </div>

          {/* Dropdowns + actions buttons */}
          <div className="flex flex-row flex-wrap items-center gap-4">
            <div className="flex flex-row items-center gap-2 sm:gap-4">
              {/* Bulk Role Dropdown */}
              <div ref={bulkRoleRef} className="relative">
                <button
                  onClick={() => {
                    setBulkRoleOpen((r) => !r);
                    setBulkStatusOpen(false);
                  }}
                  className={`inline-flex items-center px-0 py-1 md:py-2 text-sm md:text-base ${
                    bulkRoleOpen
                      ? "border-b-2 border-blue-500 text-primary"
                      : "border-b-2 border-transparent text-gray-700 hover:border-gray-400"
                  } focus:outline-none`}
                >
                  {bulkRole
                    ? Object.values(ROLE).find(
                        (role) => role.value === bulkRole
                      )?.label
                    : "Role"}
                  <FiChevronDown
                    className={`w-5 h-5 ml-2 transform transition-transform duration-200 ${
                      bulkRoleOpen ? "rotate-180" : "rotate-0"
                    }`}
                  />
                </button>

                {/* Dropdown menu */}
                <div
                  className={`absolute left-0 z-50 mt-2 min-w-full md:w-48 bg-white divide-y divide-gray-100 rounded-lg shadow transform transition-all duration-200 origin-top ${
                    bulkRoleOpen
                      ? "opacity-100 scale-100 pointer-events-auto"
                      : "opacity-0 scale-95 pointer-events-none"
                  } max-h-60 overflow-auto`}
                >
                  <ul className="py-2 text-base text-gray-700">
                    {Object.values(ROLE)
                      .filter((role) => role.value !== "ALL")
                      .map((role) => (
                        <li key={role.value}>
                          <button
                            className={`w-full px-4 py-2 text-left hover:bg-gray-100 ${
                              bulkRole === role.value
                                ? "bg-gray-100 font-medium"
                                : ""
                            }`}
                            onClick={() => {
                              setBulkRole(role.value);
                              setBulkRoleOpen(false);
                            }}
                          >
                            {role.label}
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>

              {/* Bulk Status Dropdown */}
              <div ref={bulkStatusRef} className="relative">
                <button
                  onClick={() => {
                    setBulkStatusOpen((s) => !s);
                    setBulkRoleOpen(false);
                  }}
                  className={`inline-flex items-center px-0 py-1 md:py-2 text-sm md:text-base ${
                    bulkStatusOpen
                      ? "border-b-2 border-blue-500 text-primary"
                      : "border-b-2 border-transparent text-gray-700 hover:border-gray-400"
                  } focus:outline-none`}
                >
                  {bulkStatus
                    ? bulkStatus === "true"
                      ? "Active"
                      : "Deactive"
                    : "Status"}
                  <FiChevronDown
                    className={`w-5 h-5 ml-2 transform transition-transform duration-200 ${
                      bulkStatusOpen ? "rotate-180" : "rotate-0"
                    }`}
                  />
                </button>

                {/* Dropdown menu */}
                <div
                  className={`absolute left-0 z-50 mt-2 min-w-full md:w-48 bg-white divide-y divide-gray-100 rounded-lg shadow transition-all duration-200 origin-top ${
                    bulkStatusOpen
                      ? "opacity-100 scale-100 pointer-events-auto"
                      : "opacity-0 scale-95 pointer-events-none"
                  } max-h-60 overflow-auto`}
                >
                  <ul className="py-2 text-base text-gray-700">
                    <li>
                      <button
                        className={`w-full px-4 py-2 text-left hover:bg-gray-100 ${
                          bulkStatus ? "bg-gray-100 font-medium" : ""
                        }`}
                        onClick={() => {
                          setBulkStatus("true");
                          setBulkStatusOpen(false);
                        }}
                      >
                        Active
                      </button>
                    </li>
                    <li>
                      <button
                        className={`w-full px-4 py-2 text-left hover:bg-gray-100 ${
                          bulkStatus === "false"
                            ? "bg-gray-100 font-medium"
                            : ""
                        }`}
                        onClick={() => {
                          setBulkStatus("false");
                          setBulkStatusOpen(false);
                        }}
                      >
                        Deactive
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-row gap-3 md:gap-4 sm:w-auto">
              {/* Apply Button*/}
              <button
                onClick={handleBulkUpdate}
                className={`hidden md:block px-4 py-1 rounded text-white text-sm md:text-base ${
                  !bulkRole && !bulkStatus
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-primary hover:bg-blue-800"
                }`}
                disabled={!bulkRole && !bulkStatus}
              >
                Apply
              </button>

              {/* Apply Icon */}
              <button
                onClick={handleBulkUpdate}
                className={`md:hidden flex items-center justify-center p-1 rounded-full ${
                  !bulkRole && !bulkStatus
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-primary hover:bg-blue-800"
                }`}
                disabled={!bulkRole && !bulkStatus}
                title="Apply"
              >
                <FaCheck className="text-white w-4 h-4" />
              </button>

              {/* Cancel Button*/}
              <button
                onClick={() => {
                  setBulkRole("");
                  setBulkStatus("");
                  setSelectedUsers([]);
                }}
                className={`hidden md:block px-4 py-1 rounded text-white text-sm md:text-base ${
                  !bulkRole && !bulkStatus
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-800"
                }`}
                disabled={!bulkRole && !bulkStatus}
              >
                Cancel
              </button>

              {/* Cancel Icon */}
              <button
                onClick={() => {
                  setBulkRole("");
                  setBulkStatus("");
                  setSelectedUsers([]);
                }}
                className={`md:hidden flex items-center justify-center p-1 rounded-full ${
                  !bulkRole && !bulkStatus
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-800"
                }`}
                disabled={!bulkRole && !bulkStatus}
                title="Cancel"
              >
                <FaTimes className="text-white w-4 h-4" />
              </button>

              {/* Show selected count (Desktop only) */}
              <span className="hidden md:block text-primary text-sm md:text-base pt-1 font-semibold">
                {selectedUsers.length}/{totalCount} selected
              </span>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-x-auto shadow-md sm:rounded-lg mt-2">
          <div
            className={`overflow-y-auto ${
              selectedUsers.length > 0
                ? "h-[60vh] md:h-[46vh]" // smaller height when bulk update is shown
                : "h-[65vh] md:h-[53vh]" // default height
            }`}
          >
            {tableLoading ? (
              <div className="flex items-center justify-center h-full">
                <Spinner />
              </div>
            ) : (
              <>
                {/* Select All for mobile view */}
                <div className="block md:hidden px-3 mx-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={
                        userData.length > 0 &&
                        selectedUsers.length === userData.length
                      }
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 focus:outline-none"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 font-semibold">
                      Select All
                    </span>
                  </label>
                </div>

                <GenericTable
                  tableColumns={tableColumns}
                  tableRows={userData}
                  columnLabels={columnLabels}
                  renderField={renderField}
                  renderMobileCard={(row) => renderMobileCard(row, renderField)}
                  className="w-full text-sm text-left text-gray-500"
                  headerClassName="text-sm text-black bg-gray-50 sticky top-0 z-10"
                  customError="No user found"
                />
              </>
            )}
          </div>
        </div>

        {/* Pagination */}
        <div className="flex justify-end pb-4 px-4 mt-2">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            rowsPerPage={rowsPerPage}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg relative">
            <h2 className="text-lg font-semibold mb-4">Edit User</h2>

            <div className="flex flex-col gap-4">
              {/* Role Dropdown */}
              <div>
                <label className="block mb-1 font-medium">
                  {USER_FORM_LABELS.role}
                </label>
                <select
                  value={selectedUser.role}
                  onChange={(e) =>
                    setSelectedUser((prev) => ({
                      ...prev,
                      role: e.target.value,
                    }))
                  }
                  className="border rounded p-2 w-full"
                >
                  {Object.values(ROLE).map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Active Dropdown */}
              <div>
                <label className="block mb-1 font-medium">
                  {USER_FORM_LABELS.is_approved}
                </label>
                <select
                  value={selectedUser.is_approved}
                  onChange={(e) =>
                    setSelectedUser((prev) => ({
                      ...prev,
                      is_approved: e.target.value === "true",
                    }))
                  }
                  className="border rounded p-2 w-full"
                >
                  <option value="true">Active</option>
                  <option value="false">Deactive</option>
                </select>
              </div>
            </div>

            {/* Action buttons */}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="bg-red-600 hover:bg-red-900 text-white px-4 py-2 rounded disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveChanges}
                className="bg-primary hover:bg-blue-900 text-white px-4 py-2 rounded disabled:cursor-not-allowed"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
